import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import './Modal.css';
import { useTranslation } from 'react-i18next';

interface Props {
  title: string;
  children: ReactNode;
  onClose: () => void;
  width?: number;
  /** Rendered in the header, e.g. a status badge. */
  headerAside?: ReactNode;
  /**
   * Whether Escape and a backdrop click close it. Default true.
   *
   * `false` is for a dialog whose content cannot be recovered once it is
   * gone — a password shown once. Closing that by a stray click means
   * resetting it to see it again.
   */
  dismissible?: boolean;
  /**
   * Locul de langa care sa se deschida, cand exista unul.
   *
   * Fereastra de programare se deschide langa intervalul apasat, ca in
   * referinta: pe o grila de douasprezece coloane, o fereastra centrata muta
   * ochiul de pe coloana in care tocmai ai apasat. Cand nu incape la dreapta
   * trece la stanga, si daca nu incape nicaieri ramane centrata.
   */
  anchor?: { left: number; right: number; top: number } | null;
}

/**
 * A modal dialog.
 *
 * Handles the parts that are easy to leave out and obvious when missing:
 * Escape closes it, focus moves inside and is trapped there, focus returns
 * where it came from, and the page behind does not scroll.
 */
/**
 * Unde se aseaza panoul cand are un punct de ancorare.
 *
 * La dreapta locului apasat daca incape, altfel la stanga, altfel centrat pe
 * el fara sa iasa din ecran. Pe verticala se aliniaza cu locul apasat, dar nu
 * coboara sub marginea de jos.
 */
function anchoredStyle(
  anchor: { left: number; right: number; top: number } | null,
  width: number,
): React.CSSProperties {
  if (!anchor || typeof window === 'undefined') return { maxWidth: width };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Sub aceasta latime fereastra oricum ocupa tot ecranul: ancorarea ar muta-o
  // peste marginea pe care telefonul n-o are.
  if (vw < 760) return { maxWidth: width };

  const gap = 10;
  const w = Math.min(width, vw - 2 * gap);
  let left = anchor.right + gap;
  if (left + w > vw - gap) left = anchor.left - w - gap;
  if (left < gap)
    left = Math.max(gap, Math.min(vw - w - gap, (anchor.left + anchor.right) / 2 - w / 2));

  const maxH = Math.round(vh * 0.86);
  const top = Math.max(gap, Math.min(anchor.top, vh - maxH - gap));
  return { maxWidth: width, width: w, position: 'fixed', left, top, margin: 0, maxHeight: maxH };
}

export function Modal({
  title,
  children,
  onClose,
  width = 520,
  headerAside,
  dismissible = true,
  anchor = null,
}: Props) {
  const { t } = useTranslation('common');
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  /*
   * `onClose` citit dintr-un ref, nu din dependențele efectului de mai jos.
   * Părinții îl dau ca funcție nouă la fiecare randare, iar efectul rula din
   * nou la fiecare: focusul sărea înapoi pe primul câmp și fereastra se
   * derula sus. O fereastră care salvează pe loc (lucrarea de laborator:
   * bifezi o verificare, tabla se reîmprospătează în spate) pierdea așa ce
   * scria omul, iar Enter ajungea în alt câmp.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    restoreFocusTo.current = document.activeElement as HTMLElement | null;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the first field, so a form opens ready to be typed into.
    //
    // It used to be the first focusable thing, which is the close button in
    // the header — „Programare nouă" opened with the X ringed and Enter on it
    // shutting the dialog. A dialog with no field (a confirmation) keeps the
    // old behaviour, because there the first control is usually the one that
    // does something irreversible.
    const panel = panelRef.current;
    const firstField = panel?.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled)',
    );
    const focusable = panel?.querySelectorAll<HTMLElement>(FOCUSABLE);
    (firstField ?? focusable?.[0] ?? panel)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (dismissible) onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      // Trap Tab inside the dialog: without this, tabbing walks out into the
      // page behind, where the user cannot see where they are.
      const items = panelRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (!items || items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      restoreFocusTo.current?.focus?.();
    };
  }, [dismissible]);

  return createPortal(
    <div
      className="modal__backdrop"
      onMouseDown={(e) => {
        // Only a click that starts on the backdrop closes. Otherwise a drag
        // that begins inside the dialog and ends outside would dismiss it,
        // losing whatever was typed.
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`modal__panel ${anchor ? 'modal__panel--anchored' : ''}`}
        style={anchoredStyle(anchor, width)}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="modal__header">
          <h2 className="modal__title">{title}</h2>
          {headerAside}
          <button
            type="button"
            className="modal__close"
            onClick={onClose}
            aria-label={t('inchide')}
          >
            <X size={18} />
          </button>
        </header>
        <div className="modal__body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
